// components/CardImageCap.tsx
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { cn } from '@/lib/utils';

type CardImageCapProps = React.ComponentProps<'div'> & {
  src?: string;
  fallbackSrc?: string;
  srcAlt?: string;
  logoSrc?: string;
  fallbackLogoSrc?: string;
  logoAlt?: string;
  isLoading?: boolean;
  orientation?: 'horizontal' | 'vertical';
  skeletonHeight?: number;
  skeletonWidth?: number;
  imageLoadingType?: 'eager' | 'lazy';
};

const DEFAULT_FALLBACK = '/images/default-card-header-light.png';

export const CardImageCap = ({
  src,
  fallbackSrc = DEFAULT_FALLBACK,
  srcAlt,
  logoSrc,
  fallbackLogoSrc,
  logoAlt,
  isLoading = false,
  orientation = 'vertical',
  skeletonHeight = 140,
  skeletonWidth,
  className,
  imageLoadingType = 'eager',
  ...props
}: CardImageCapProps) => {
  const [shouldShowImageCap, setShouldShowImageCap] = useState(false);
  const [shouldShowLogoCap, setShouldShowLogoCap] = useState(false);
  const [didTryFallback, setDidTryFallback] = useState(false);

  const orientationClassName =
    orientation === 'horizontal' ? 'md:tw-w-60 md:tw-h-full tw-h-[140px]' : '';

  const handleSrcFallback = (
    event: React.SyntheticEvent<HTMLImageElement>,
    altSrc?: string,
    imageKey?: string,
  ) => {
    if (didTryFallback) {
      return;
    }

    setDidTryFallback(true);
    const { currentTarget } = event;
    if (!altSrc || currentTarget.src.endsWith(altSrc)) {
      if (imageKey === 'imageCap') {
        currentTarget.src = DEFAULT_FALLBACK;
      } else {
        setShouldShowLogoCap(false);
      }

      return;
    }
    currentTarget.src = altSrc;
  };

  if (isLoading) {
    return (
      <div
        data-slot="card-image-cap"
        className={cn('tw-relative', orientationClassName, className)}
        data-testid="image-loader-wrapper"
        {...props}
      >
        <Skeleton
          containerClassName="tw-block tw-leading-none"
          height={orientation === 'horizontal' ? '100%' : skeletonHeight}
          width={skeletonWidth}
        />
      </div>
    );
  }

  return (
    <div
      data-slot="card-image-cap"
      className={cn('tw-relative', orientationClassName, className)}
      {...props}
    >
      {src && (
        <img
          className={cn(
            'tw-w-full tw-object-cover tw-transition-opacity tw-h-full',
            orientationClassName,
            shouldShowImageCap ? 'tw-opacity-100' : 'tw-opacity-0',
          )}
          src={src}
          onError={(e) => handleSrcFallback(e, fallbackSrc, 'imageCap')}
          onLoad={() => setShouldShowImageCap(true)}
          alt={srcAlt}
          loading={imageLoadingType}
        />
      )}
      {logoSrc && (
        <img
          className={cn(
            'tw-absolute tw-bottom-2 tw-left-2 tw-transition-opacity',
            orientationClassName,
            shouldShowLogoCap ? 'tw-opacity-100' : 'tw-opacity-0',
          )}
          src={logoSrc}
          onError={(e) => handleSrcFallback(e, fallbackLogoSrc, 'logoCap')}
          onLoad={() => setShouldShowLogoCap(true)}
          alt={logoAlt}
          loading={imageLoadingType}
        />
      )}
    </div>
  );
};
